import { useMemo } from "react";
import type { User, Session } from "better-auth/types";
import { usePermissions as useSharedPermissions } from "@charphq/permissions";
import type { ExtendedUser, ExtendedSession } from "@charphq/permissions";

// Re-export Better Auth types for convenience
export type { User, Session } from "better-auth/types";

// Re-export shared permission types
export type { ExtendedUser, ExtendedSession } from "@charphq/permissions";

// Authentication state types
export interface AuthState {
  user: ExtendedUser | null;
  session: ExtendedSession | null;
  isLoading: boolean;
  error: Error | null;
}

// Session data structure from better-auth
export interface SessionData {
  user: User;
  session: Session;
}

// Generic Better Auth client interface
export interface BetterAuthClient {
  useSession: () => {
    data: SessionData | null;
    isPending: boolean;
    error: Error | null;
    refetch: () => void;
  };
  signIn: any;
  signUp: any;
  signOut: any;
  organization?: any;
  twoFactor?: any;
}

/**
 * Generic useAuth hook that works with any Better Auth client
 * This hook provides a convenient interface for authentication state
 * and wraps the Better Auth useSession hook with additional utilities.
 *
 * The return value is memoized to prevent unnecessary re-renders in consuming components.
 */
export function useAuth(authClient: BetterAuthClient): AuthState & {
  isAuthenticated: boolean;
  isGuest: boolean;
  user: ExtendedUser | null;
  session: ExtendedSession | null;
} {
  const { data: sessionData, isPending: isLoading, error } = authClient.useSession();

  // Extract values for memoization dependencies
  const user = sessionData?.user as ExtendedUser | null;
  const session = sessionData as ExtendedSession | null;
  const isAuthenticated = !!sessionData?.user;
  const isGuest = !sessionData?.user;

  // Memoize the return object to prevent unnecessary re-renders
  // when the underlying values haven't actually changed
  return useMemo(
    () => ({
      user,
      session,
      isLoading,
      error,
      isAuthenticated,
      isGuest,
    }),
    [user, session, isLoading, error, isAuthenticated, isGuest],
  );
}

/**
 * Hook for checking if user has a specific role or permission
 * Now uses the shared permissions package
 */
export function usePermissions(authClient: BetterAuthClient) {
  const { user, session } = useAuth(authClient);
  return useSharedPermissions(user, session);
}

/**
 * Hook for organization-related functionality
 */
export function useOrganization(authClient: BetterAuthClient) {
  const { user, isAuthenticated } = useAuth(authClient);

  const isOrganizationMember = (organizationId: string): boolean => {
    if (!isAuthenticated || !user) return false;
    // This would need to be implemented based on your organization structure
    // For now, returning false as a placeholder
    void organizationId; // Avoid unused parameter warning
    return false;
  };

  const getUserOrganizations = () => {
    if (!isAuthenticated || !user) return [];
    // This would need to be implemented based on your organization structure
    // For now, returning empty array as a placeholder
    return [];
  };

  return {
    isOrganizationMember,
    getUserOrganizations,
  };
}
